import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Td,
  Tbody,
  Text,
  Heading,
  Button,
  Grid,
  Box,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Icon,
  Select,
  useColorModeValue,
  Divider,
} from "@chakra-ui/react";
import { cond, filter, set, last, find, sortBy as sort } from "lodash";
import DynamicForm from "../../../../components/DynamicForm";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { useLocalStorage } from "useLocalStorage";
import DealCalculations from "./DealCalculations";
import DatePicker from "react-date-picker";
import { SingleDatepicker, RangeDatepicker } from "chakra-dayzed-datepicker";
import {
  exportComponentAsJPEG,
  exportComponentAsPDF,
  exportComponentAsPNG,
} from "react-component-export-image";
import { FaListAlt, FaBalanceScale, FaCalendarAlt } from "react-icons/fa";
import { CurrencyFormatter, FormatCurrencyPlain, round2 } from "./trustReconciliationUtils";
import MonthlyTrustReconciliation from "./MonthlyTrustReconciliation";

const Fields = [
  {
    type: "select",
    label: "Account",
    name: "account",
    placeholder: "Select An Account",
    required: true,
    options: [
      {
        label: "Real Estate Trust",
        value: "real_estate_trust",
      },
      {
        label: "Commission Trust",
        value: "commission_trust",
      },
      {
        label: "General",
        value: "general",
      },
    ],
  },
  { type: "text", label: "Details", name: "details" },
  {
    type: "date",
    label: "Date",
    name: "date",
    placeholder: "Select Date",
  },
  { type: "text", label: "Reference", name: "reference" },
  { type: "number", label: "Amount", name: "amount" },
  {
    type: "select",
    label: "Type",
    name: "type",
    placeholder: "Select An Type",
    required: true,
    options: [
      {
        label: "Debit",
        value: "debit",
      },
      {
        label: "Credit",
        value: "credit",
      },
    ],
  },
  {
    type: "select",
    label: "To",
    name: "to",
    placeholder: "Select...",
    conditions: [{ name: "type", value: "credit" }],
    options: [
      {
        label: "Commission Trust",
        value: "commission_trust",
      },
      {
        label: "Dwelly General",
        value: "general",
      },
      {
        label: "Buyer",
        value: "buyer",
      },
      {
        label: "Buyer Rebate",
        value: "buyer_rebate",
      },
      {
        label: "Seller",
        value: "seller",
      },
      {
        label: "Owner",
        value: "owner",
      },
      {
        label: "Seller Rebate",
        value: "seller_rebate",
      },
      {
        label: "Seller Lawyer",
        value: "seller_lawyer",
      },
      {
        label: "Dwelly Agent",
        value: "agent_dwelly",
      },
      {
        label: "Other Agent",
        value: "agent_other",
      },
    ],
  },
];

const AllTransactions = ({
  dealId,
  deal,
  allAgents: propAgents,
  deals: propDeals,
  auth: propAuth,
}) => {
  const [transactions, setTransactions] = useState(null);
  const [deals, setDeals] = useState(propDeals || []);
  const [allAgents, setAllAgents] = useState(propAgents || []);
  const [dirtyTxns, setDirtyTxns] = useState([]);
  const [RSTrust, setRSTrust] = useState([]);
  const [CTrust, setCTrust] = useState([]);
  const [General, setGeneral] = useState([]);
  const [txnId, setTxnId] = useState(null);
  const [txnData, setTxnData] = useState(null);
  const [localAuth] = useLocalStorage("auth_v2", null);
  const auth = propAuth || localAuth;

  // Ledger Filter Controls: Month, Year, Presets, and Date Range
  const [quickPreset, setQuickPreset] = useState("all_since_2025");
  const [ledgerYear, setLedgerYear] = useState("all");
  const [ledgerMonth, setLedgerMonth] = useState("all");

  const [selectedDates, setSelectedDates] = useState([
    new Date("2025-01-01T00:00:00"),
    new Date(),
  ]);

  const rstREf = useRef();
  const ctRef = useRef();
  const gRef = useRef();

  const textColor = useColorModeValue("gray.700", "white");
  const tabBg = useColorModeValue("white", "gray.800");
  const filterBg = useColorModeValue("gray.50", "gray.750");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Extract available years from transactions
  const availableLedgerYears = useMemo(() => {
    const yearsSet = new Set([2025, 2026, moment().year()]);
    if (transactions && transactions.length > 0) {
      transactions.forEach((t) => {
        if (t.date) {
          const yr = moment(t.date).year();
          if (yr && !isNaN(yr)) yearsSet.add(yr);
        }
      });
    }
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [transactions]);

  // Handle Quick Presets
  const handlePresetChange = (e) => {
    const val = e.target.value;
    setQuickPreset(val);

    if (val === "this_month") {
      const start = moment().startOf("month").toDate();
      const end = moment().endOf("month").toDate();
      setSelectedDates([start, end]);
      setLedgerYear(String(moment().year()));
      setLedgerMonth(String(moment().month()));
    } else if (val === "last_month") {
      const start = moment().subtract(1, "month").startOf("month").toDate();
      const end = moment().subtract(1, "month").endOf("month").toDate();
      setSelectedDates([start, end]);
      setLedgerYear(String(moment().subtract(1, "month").year()));
      setLedgerMonth(String(moment().subtract(1, "month").month()));
    } else if (val === "ytd") {
      const start = moment().startOf("year").toDate();
      const end = moment().toDate();
      setSelectedDates([start, end]);
      setLedgerYear(String(moment().year()));
      setLedgerMonth("all");
    } else if (val === "full_2026") {
      const start = new Date("2026-01-01T00:00:00");
      const end = new Date("2026-12-31T23:59:59");
      setSelectedDates([start, end]);
      setLedgerYear("2026");
      setLedgerMonth("all");
    } else if (val === "full_2025") {
      const start = new Date("2025-01-01T00:00:00");
      const end = new Date("2025-12-31T23:59:59");
      setSelectedDates([start, end]);
      setLedgerYear("2025");
      setLedgerMonth("all");
    } else if (val === "all_since_2025") {
      const start = new Date("2025-01-01T00:00:00");
      const end = moment().endOf("month").toDate();
      setSelectedDates([start, end]);
      setLedgerYear("all");
      setLedgerMonth("all");
    }
  };

  // Handle Year Change
  const handleYearChange = (e) => {
    const yr = e.target.value;
    setLedgerYear(yr);
    setQuickPreset("custom");

    if (yr === "all") {
      if (ledgerMonth === "all") {
        setSelectedDates([new Date("2025-01-01T00:00:00"), moment().endOf("month").toDate()]);
      } else {
        const m = parseInt(ledgerMonth);
        const currentYr = moment().year();
        const start = moment(`${currentYr}-${String(m + 1).padStart(2, "0")}-01`).startOf("month").toDate();
        const end = moment(start).endOf("month").toDate();
        setSelectedDates([start, end]);
      }
    } else {
      const yNum = parseInt(yr);
      if (ledgerMonth === "all") {
        const start = moment(`${yNum}-01-01`).startOf("year").toDate();
        const end = moment(`${yNum}-12-31`).endOf("year").toDate();
        setSelectedDates([start, end]);
      } else {
        const m = parseInt(ledgerMonth);
        const start = moment(`${yNum}-${String(m + 1).padStart(2, "0")}-01`).startOf("month").toDate();
        const end = moment(start).endOf("month").toDate();
        setSelectedDates([start, end]);
      }
    }
  };

  // Handle Month Change
  const handleMonthChange = (e) => {
    const mo = e.target.value;
    setLedgerMonth(mo);
    setQuickPreset("custom");

    const yr = ledgerYear === "all" ? moment().year() : parseInt(ledgerYear);

    if (mo === "all") {
      if (ledgerYear === "all") {
        setSelectedDates([new Date("2025-01-01T00:00:00"), moment().endOf("month").toDate()]);
      } else {
        const start = moment(`${yr}-01-01`).startOf("year").toDate();
        const end = moment(`${yr}-12-31`).endOf("year").toDate();
        setSelectedDates([start, end]);
      }
    } else {
      const mNum = parseInt(mo);
      const start = moment(`${yr}-${String(mNum + 1).padStart(2, "0")}-01`).startOf("month").toDate();
      const end = moment(start).endOf("month").toDate();
      setSelectedDates([start, end]);
    }
  };

  const startDateLimit = useMemo(() => moment(selectedDates[0]).startOf("day"), [selectedDates]);
  const endDateLimit = useMemo(() => moment(selectedDates[1]).endOf("day"), [selectedDates]);

  const balanceForward = (list) => {
    const total = list
      .filter((txn) => {
        return txn.date && moment(txn.date).isBefore(startDateLimit, "day");
      })
      .reduce((acc, t) => {
        const amt = round2(t.amount);
        return t.type === "debit" ? acc + amt : acc - amt;
      }, 0);
    return round2(total);
  };

  const balanceToDate = (list) => {
    const total = list
      .filter((txn) => {
        return txn.date && moment(txn.date).isSameOrBefore(endDateLimit, "day");
      })
      .reduce((acc, t) => {
        const amt = round2(t.amount);
        return t.type === "debit" ? acc + amt : acc - amt;
      }, 0);
    return round2(total);
  };

  const txnVisible = (txn) => {
    return (
      txn.date &&
      moment(txn.date).isSameOrAfter(startDateLimit, "day") &&
      moment(txn.date).isSameOrBefore(endDateLimit, "day")
    );
  };

  const txn = {
    _id: "_id",
    account: "",
    amount: 0,
    details: "",
    date: undefined,
    reference: "",
    type: "debit",
    to: "commission_trust",
  };

  const titles = {
    real_estate_trust: "Real Estate Trust Account Ledger",
    commission_trust: "Commission Trust Account Ledger",
    general: "General Account Ledger",
  };

  useEffect(() => {
    if (auth && auth.token) {
      // Fetch all transactions
      fetch(`/api/transactions`, {
        headers: {
          dwelly_token: auth.token,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          setTransactions(data);
        })
        .catch((e) => {
          console.log(e);
          setTransactions([]);
        });

      // Fetch deals if not passed as prop
      if (!propDeals || propDeals.length === 0) {
        fetch(`/api/deals`, {
          headers: {
            dwelly_token: auth.token,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (Array.isArray(data)) {
              setDeals(data);
            }
          })
          .catch((e) => console.log("Failed to fetch deals:", e));
      } else {
        setDeals(propDeals);
      }

      // Fetch agents if not passed as prop
      if (!propAgents || propAgents.length === 0) {
        fetch(`/api/agents`, {
          headers: {
            dwelly_token: auth.token,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data && data.agents) {
              setAllAgents(data.agents);
            }
          })
          .catch((e) => console.log("Failed to fetch agents:", e));
      } else {
        setAllAgents(propAgents);
      }
    }
  }, [auth, propDeals, propAgents]);

  useEffect(() => {
    if (txnId) {
      if (txnId === "_id") {
        setTxnData({ ...txn, _id: uuidv4() });
      } else {
        const txn = find(transactions, { _id: txnId });
        console.log("debug", { txn });
        setTxnData(txn);
      }
    } else {
      setTxnData(null);
    }
  }, [txnId]);

  useEffect(() => {
    if (transactions && transactions.length > 0) {
      setRSTrust(
        sort(filter([...transactions], { account: "real_estate_trust" }), [
          function (o) {
            return o.date ? new Date(o.date) : new Date();
          },
        ])
      );
      setCTrust(
        sort(filter([...transactions], { account: "commission_trust" }), [
          function (o) {
            return o.date ? new Date(o.date) : new Date();
          },
        ])
      );
      setGeneral(
        sort(filter([...transactions], { account: "general" }), [
          function (o) {
            return o.date ? new Date(o.date) : new Date();
          },
        ])
      );
      setDirtyTxns(filter([...transactions], { dirty: true }));
    }
  }, [transactions]);

  const dealsMap = useMemo(() => {
    const map = {};
    (deals || []).forEach((d) => {
      if (d._id) map[String(d._id)] = d;
      if (d.deal_id) map[String(d.deal_id)] = d;
    });
    return map;
  }, [deals]);

  const getDealNoForTxn = (transaction) => {
    if (transaction.deal_no) return String(transaction.deal_no);
    if (transaction.deal_id) {
      const match = dealsMap[String(transaction.deal_id)];
      if (match && match.deal_id) return String(match.deal_id);
      return String(transaction.deal_id);
    }
    if (transaction.dealId) {
      const match = dealsMap[String(transaction.dealId)];
      if (match && match.deal_id) return String(match.deal_id);
      return String(transaction.dealId);
    }
    if (transaction.reference) {
      const ref = String(transaction.reference);
      const match = (deals || []).find(
        (d) =>
          d.deal_id &&
          (ref.includes(String(d.deal_id)) || (d._id && ref.includes(String(d._id))))
      );
      if (match && match.deal_id) return String(match.deal_id);
    }
    return "-";
  };

  const getTable = (title, data, ref) => {
    const toText = find(Fields, { name: "to" }).options;
    const balanceStart = balanceForward(data);
    const balanceEnd = balanceToDate(data);
    const visibleData = data.filter(txnVisible);

    // Calculate running balance for each transaction in the period with exact 2-decimal rounding
    let currentBalance = balanceStart;
    const rowsWithBalance = visibleData.map((transaction) => {
      const amt = round2(transaction.amount);
      if (transaction.type === "debit") {
        currentBalance = round2(currentBalance + amt);
      } else {
        currentBalance = round2(currentBalance - amt);
      }
      return {
        transaction,
        runningBalance: currentBalance,
      };
    });

    return (
      <div ref={ref} style={{ width: "100%", paddingLeft: "5px", paddingRight: "5px", marginBottom: "25px" }}>
        <Flex justify="space-between" align="center" pt="4" pb="2">
          <Heading
            as={"h2"}
            size={"sm"}
            color="teal.700"
            onClick={() => {
              exportComponentAsPNG(ref, {
                fileName: title,
                pdfOptions: {
                  format: "letter",
                },
              });
            }}
            cursor="pointer"
            _hover={{ color: "teal.500" }}
          >
            {titles[title]} <Text as="span" fontSize="2xs" color="gray.500">(Click to Export PNG)</Text>
          </Heading>

          <Text fontSize="xs" color="gray.500">
            {visibleData.length} transactions in period
          </Text>
        </Flex>

        <TableContainer border="1px" borderColor="gray.200" borderRadius="md" boxShadow="xs">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Txn Date</Th>
                <Th>Deal #</Th>
                <Th>Details</Th>
                <Th>Reference</Th>
                <Th>To</Th>
                <Th isNumeric>Amount</Th>
                <Th isNumeric>Balance</Th>
              </Tr>
            </Thead>
            <Tbody>
              {/* Balance Forward */}
              <Tr bg="gray.50">
                <Td></Td>
                <Td></Td>
                <Td></Td>
                <Td></Td>
                <Td fontWeight="bold" textAlign={"right"} fontSize="xs" color="gray.600">
                  Balance Forward (Prior to {startDateLimit.format("ll")}):
                </Td>
                <Td fontSize="xs" isNumeric color="gray.400">-</Td>
                <Td fontWeight="bold" isNumeric fontSize="xs" color="gray.700">
                  {CurrencyFormatter(balanceStart)}
                </Td>
              </Tr>

              {rowsWithBalance.map(({ transaction, runningBalance }, idx) => {
                const toLabel = find(toText, { value: transaction.to });
                const amount = FormatCurrencyPlain(transaction.amount);
                const dealNo = getDealNoForTxn(transaction);
                return (
                  <Tr key={`all_txn_${title}_${idx}`} _hover={{ bg: "gray.50" }}>
                    <Td fontSize="xs" whiteSpace="nowrap">
                      {transaction.date ? moment(transaction.date).format("ll") : "-"}
                    </Td>
                    <Td fontSize="xs" fontWeight="semibold">
                      {dealNo !== "-" ? (
                        <Badge colorScheme="teal" variant="subtle" fontSize="2xs" px="2" py="0.5" borderRadius="md">
                          #{dealNo}
                        </Badge>
                      ) : (
                        <Text as="span" color="gray.400" fontSize="xs">-</Text>
                      )}
                    </Td>
                    <Td fontSize="xs">{transaction.details || "-"}</Td>
                    <Td fontSize="xs">{transaction.reference || "-"}</Td>
                    <Td fontSize="xs">{toLabel && toLabel.label ? toLabel.label : "-"}</Td>
                    <Td
                      isNumeric
                      fontSize="xs"
                      fontWeight="medium"
                      color={transaction.type === "debit" ? "green.600" : "red.600"}
                    >
                      {transaction.type === "debit" ? amount : "(" + amount + ")"}
                    </Td>
                    <Td
                      isNumeric
                      fontSize="xs"
                      fontWeight="semibold"
                      color={runningBalance < 0 ? "red.600" : "gray.700"}
                    >
                      {CurrencyFormatter(runningBalance)}
                    </Td>
                  </Tr>
                );
              })}

              {visibleData.length === 0 && (
                <Tr>
                  <Td colSpan={7} textAlign="center" py="4" color="gray.500" fontSize="xs">
                    No transactions recorded in this period for {titles[title]}.
                  </Td>
                </Tr>
              )}

              {/* Period Closing Balance */}
              <Tr bg="gray.50" fontWeight="bold">
                <Td></Td>
                <Td></Td>
                <Td></Td>
                <Td></Td>
                <Td textAlign={"right"} fontSize="xs" color="teal.800">
                  Closing Balance (as of {endDateLimit.format("ll")}):
                </Td>
                <Td fontSize="xs" isNumeric color="gray.400">-</Td>
                <Td isNumeric color="teal.700" fontSize="sm">
                  {CurrencyFormatter(balanceEnd)}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  return (
    <Box w="100%">
      <Tabs colorScheme="teal" variant="enclosed" isLazy>
        <TabList mb="4" borderBottom="2px" borderColor="teal.500">
          <Tab fontWeight="bold" fontSize="sm" _selected={{ color: "teal.600", borderColor: "teal.500", borderBottomColor: tabBg }}>
            <Flex align="center" gap="2">
              <Icon as={FaBalanceScale} />
              <Text>Monthly Trust Reconciliation (O. Reg. 579/05)</Text>
              <Badge colorScheme="green" ml="1" fontSize="0.7rem">
                TRESA
              </Badge>
            </Flex>
          </Tab>
          <Tab fontWeight="bold" fontSize="sm" _selected={{ color: "teal.600", borderColor: "teal.500", borderBottomColor: tabBg }}>
            <Flex align="center" gap="2">
              <Icon as={FaListAlt} />
              <Text>Transactions Ledger (All Accounts)</Text>
            </Flex>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Tab 1: Monthly Trust Reconciliation (O. Reg. 579/05 Section 13) */}
          <TabPanel px="0" py="2">
            <MonthlyTrustReconciliation
              transactions={transactions || []}
              deals={deals || []}
              allAgents={allAgents || []}
              auth={auth}
            />
          </TabPanel>

          {/* Tab 2: Transactions Ledger */}
          <TabPanel px="0" py="2">
            <Box p="4">
              <Flex justify="space-between" align="center" mb="4" wrap="wrap" gap="3">
                <Box>
                  <Heading size="md" color={textColor}>
                    General & Trust Transactions Ledger
                  </Heading>
                  <Text fontSize="xs" color="gray.500" mt="1">
                    Select a Month, Year, Quick Preset, or custom Date Range to filter chronological ledger activity across all accounts.
                  </Text>
                </Box>
              </Flex>

              {/* Enhanced Period Selector Bar (Month, Year, Quick Presets & Date Range) */}
              <Box p="4" mb="5" bg={filterBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="xs">
                <Flex
                  direction={{ base: "column", lg: "row" }}
                  justify="space-between"
                  align={{ base: "stretch", lg: "flex-end" }}
                  gap="4"
                >
                  <Flex
                    direction={{ base: "column", sm: "row" }}
                    gap="3"
                    align={{ base: "stretch", sm: "flex-end" }}
                    wrap="wrap"
                  >
                    {/* Quick Presets */}
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" mb="1" color="gray.600">
                        Period Preset:
                      </Text>
                      <Select
                        size="sm"
                        w={{ base: "100%", sm: "170px" }}
                        fontWeight="medium"
                        value={quickPreset}
                        onChange={handlePresetChange}
                      >
                        <option value="all_since_2025">All Since Jan 2025</option>
                        <option value="this_month">This Month</option>
                        <option value="last_month">Last Month</option>
                        <option value="ytd">Year to Date (YTD)</option>
                        <option value="full_2026">Full Year 2026</option>
                        <option value="full_2025">Full Year 2025</option>
                        <option value="custom">Custom Date Range</option>
                      </Select>
                    </Box>

                    {/* Year Selector */}
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" mb="1" color="gray.600">
                        Select Year:
                      </Text>
                      <Select
                        size="sm"
                        w={{ base: "100%", sm: "130px" }}
                        value={ledgerYear}
                        onChange={handleYearChange}
                      >
                        <option value="all">All Years</option>
                        {availableLedgerYears.map((yr) => (
                          <option key={`ly_${yr}`} value={yr}>
                            {yr}
                          </option>
                        ))}
                      </Select>
                    </Box>

                    {/* Month Selector */}
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" mb="1" color="gray.600">
                        Select Month:
                      </Text>
                      <Select
                        size="sm"
                        w={{ base: "100%", sm: "160px" }}
                        value={ledgerMonth}
                        onChange={handleMonthChange}
                      >
                        <option value="all">Full Year (All Months)</option>
                        <option value="0">January</option>
                        <option value="1">February</option>
                        <option value="2">March</option>
                        <option value="3">April</option>
                        <option value="4">May</option>
                        <option value="5">June</option>
                        <option value="6">July</option>
                        <option value="7">August</option>
                        <option value="8">September</option>
                        <option value="9">October</option>
                        <option value="10">November</option>
                        <option value="11">December</option>
                      </Select>
                    </Box>
                  </Flex>

                  {/* Custom Date Range Picker */}
                  <Box minW={{ base: "100%", lg: "280px" }}>
                    <Flex justify="space-between" align="center" mb="1">
                      <Text fontSize="xs" fontWeight="bold" color="gray.600">
                        Active Date Range:
                      </Text>
                      <Badge colorScheme="teal" fontSize="2xs" px="1.5" py="0.5" borderRadius="sm">
                        {startDateLimit.format("ll")} – {endDateLimit.format("ll")}
                      </Badge>
                    </Flex>
                    <RangeDatepicker
                      selectedDates={selectedDates}
                      onDateChange={(dates) => {
                        setSelectedDates(dates);
                        setQuickPreset("custom");
                      }}
                    />
                  </Box>
                </Flex>
              </Box>

              {/* Account Ledger Tables */}
              {getTable("real_estate_trust", RSTrust, rstREf)}
              {getTable("commission_trust", CTrust, ctRef)}
              {getTable("general", General, gRef)}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default AllTransactions;
